import {
  subscribe,
  setPartyLocation,
  togglePartyMember,
  clearParty,
  addPartyMembers,
  getState
} from '../state/store.js';
import { formatDiscordMention } from '../utils/discord.js';
import { initFormPillGroups, updateFormPillSummary } from '../utils/pill-select.js';

const openPartyBtn = document.getElementById('openPartyBtn');
const partyModal = document.getElementById('partyModal');
const partyCloseBtn = document.getElementById('partyCloseBtn');
const partyLocationSelect = document.getElementById('partyLocationSelect');
const partyPlayerFilterGroup = partyModal?.querySelector('[data-form-players-select]');
const partyPlayerFilterValue = document.getElementById('partyPlayerFilterValue');
const partyAvailable = document.getElementById('partyAvailable');
const partySelected = document.getElementById('partySelected');
const partyCopyBtn = document.getElementById('partyCopyBtn');
const partySaveBtn = document.getElementById('partySaveBtn');
const partyClearBtn = document.getElementById('partyClearBtn');
const partySelectAllBtn = document.getElementById('partySelectAllBtn');
const partyCount = document.getElementById('partyCount');
const partySelectedMeta = document.getElementById('partySelectedMeta');

const statusIsAlive = (status) => String(status || '').toLowerCase().includes('alive');
const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const getSelectedPlayerNames = () =>
  new Set(
    (partyPlayerFilterValue?.value || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  );

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const showCopiedState = (button, fallbackText) => {
  const originalText = button?.textContent || fallbackText;
  if (button) button.textContent = 'Copied';
  window.setTimeout(() => {
    if (button) button.textContent = originalText;
  }, 1600);
};

const renderOptions = (options, current) => {
  partyLocationSelect.innerHTML = options
    .map((loc) => `<option value="${loc}" ${loc === current ? 'selected' : ''}>${loc}</option>`)
    .join('');
};

const getBaseCandidates = (state) =>
  state.characters.filter((character) => {
    return character.current_location === state.party.location && statusIsAlive(character.status);
  });

const updatePlayerFilter = (characters) => {
  if (!partyPlayerFilterGroup || !partyPlayerFilterValue) return;
  const list = partyPlayerFilterGroup.querySelector('.pill-list');
  if (!list) return;
  const players = Array.from(new Set(characters.map((character) => character.player).filter(Boolean))).sort();
  list.innerHTML = players
    .map((player) => `<button type="button" class="pill" data-pill-value="${escapeHTML(player)}">${escapeHTML(player)}</button>`)
    .join('');

  const current = getSelectedPlayerNames();
  const stillValid = new Set(players.filter((player) => current.has(player)));
  partyPlayerFilterValue.value = Array.from(stillValid).join(', ');
  updateFormPillSummary(partyPlayerFilterGroup, stillValid);
};

const getFilteredCandidates = (state) => {
  const base = getBaseCandidates(state);
  const selectedPlayers = getSelectedPlayerNames();
  return base.filter((character) => {
    if (selectedPlayers.size && !selectedPlayers.has(character.player)) return false;
    return true;
  });
};

const renderPartyLists = (state) => {
  const locations = Array.from(
    new Set(state.characters.map((character) => character.current_location).filter(Boolean))
  ).sort();
  if (!state.party.location && locations.length) {
    setPartyLocation(locations[0]);
    return;
  }
  renderOptions(locations, state.party.location);
  updatePlayerFilter(getBaseCandidates(state));

  const filtered = getFilteredCandidates(state);

  const available = filtered.filter((character) => !state.party.members.has(character.slug));
  const selected = filtered.filter((character) => state.party.members.has(character.slug));

  const renderItem = (character, selectedList = false) => {
    const playerLabel = character.player || 'Unknown Player';
    const classLabel = character.class || 'Classless';
    const levelLabel = character.level || '?';

    return `<li data-slug="${character.slug}">
      <div>
        <strong>${character.name}</strong>
        <span>${playerLabel} &middot; ${classLabel} &middot; Lv ${levelLabel}</span>
      </div>
      <button type="button" class="btn ${selectedList ? 'outline' : 'primary'}" data-action="${
        selectedList ? 'remove' : 'add'
      }">
        ${selectedList ? 'Remove' : 'Add'}
      </button>
    </li>`;
  };

  partyAvailable.innerHTML = available.length
    ? available.map((character) => renderItem(character, false)).join('')
    : '<li class="muted">No characters available.</li>';

  partySelected.innerHTML = selected.length
    ? selected.map((character) => renderItem(character, true)).join('')
    : '<li class="muted">No members selected.</li>';

  if (partyCount) partyCount.textContent = `${selected.length} member${selected.length === 1 ? '' : 's'}`;
  if (partySelectedMeta) {
    const players = Array.from(new Set(selected.map((character) => character.player).filter(Boolean)));
    partySelectedMeta.textContent = players.length ? players.join(', ') : '';
  }
};

const handleListClick = (event) => {
  const item = event.target.closest('li[data-slug]');
  if (!item) return;
  togglePartyMember(item.dataset.slug);
};

const formatPartySummary = (state, selected) => {
  const location = state.party.location || 'Unknown location';
  const lines = [
    `**Party Build: ${location}**`,
    `Members: **${selected.length}**`,
    '',
    '**Party Members**'
  ];

  selected
    .slice()
    .sort(sortByName)
    .forEach((character) => {
      const mention = formatDiscordMention(character.discord_id);
      const mentionText = mention ? `${mention} ` : '';
      const classLabel = character.class || 'Classless';
      const levelLabel = character.level || '?';
      lines.push(`- ${mentionText}**${character.name || 'Unnamed'}** - ${classLabel} Lv ${levelLabel}`);
    });

  return lines.join('\n');
};

const getSelectedMembers = (state) =>
  getFilteredCandidates(state).filter((character) => state.party.members.has(character.slug));

const copyPartySummary = async () => {
  const state = getState();
  const selected = getSelectedMembers(state);
  if (!selected.length) {
    alert('Select at least one member.');
    return;
  }

  try {
    await copyText(formatPartySummary(state, selected));
    showCopiedState(partyCopyBtn, 'Copy Summary');
  } catch (error) {
    alert(`Copy failed: ${error.message}`);
  }
};

const abilityScoreRow = (character) =>
  ['str', 'dex', 'con', 'int', 'wis', 'cha']
    .map(
      (key) => `<div><span class="label">${key.toUpperCase()}</span><span class="value">${escapeHTML(character[key] ?? '—')}</span></div>`
    )
    .join('');

const buildPartySheetHTML = (state, selected) => {
  const location = state.party.location || 'Unknown location';
  const members = selected
    .slice()
    .sort(sortByName)
    .map(
      (character) => `<section class="member">
        <h2>${escapeHTML(character.name || 'Unnamed')}</h2>
        <p class="meta">${escapeHTML(character.player || 'Unknown Player')} &middot; ${escapeHTML(character.class || 'Classless')} Lv ${escapeHTML(character.level ?? '?')} &middot; ${escapeHTML(character.status || 'Unknown')}</p>
        <div class="stats-row">
          <div><span class="label">HP</span><span class="value">${escapeHTML(character.hp ?? '—')}</span></div>
          ${abilityScoreRow(character)}
        </div>
        <p class="loc">Location: ${escapeHTML(character.current_location || 'Unknown')}</p>
      </section>`
    )
    .join('');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Party — ${escapeHTML(location)}</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; background: #f7f0df; color: #2a2015; margin: 0; padding: 2rem; }
  h1 { font-size: 1.6rem; margin: 0 0 0.25rem; }
  .subtitle { color: #7d7979; margin: 0 0 1.5rem; }
  .toolbar { margin-bottom: 1.5rem; }
  .toolbar button {
    font-family: inherit; font-size: 0.9rem; padding: 0.5rem 1rem;
    border: 1px solid #c28d41; background: transparent; color: #a06f24;
    border-radius: 3px; cursor: pointer;
  }
  .member {
    border: 1px solid rgba(42, 32, 21, 0.16); border-radius: 6px;
    padding: 1rem 1.25rem; margin-bottom: 1rem; background: #efe6cd;
    page-break-inside: avoid;
  }
  .member h2 { margin: 0 0 0.2rem; font-size: 1.2rem; }
  .meta { margin: 0 0 0.75rem; color: #7d7979; font-size: 0.9rem; }
  .stats-row { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
  .stats-row > div { text-align: center; min-width: 44px; }
  .stats-row .label { display: block; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: #7d7979; }
  .stats-row .value { display: block; font-size: 1.05rem; font-weight: 700; }
  .loc { margin: 0; font-size: 0.85rem; color: #7d7979; }
  @media print {
    .toolbar { display: none; }
    body, .member { background: #fff; }
  }
</style>
</head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Print</button></div>
  <h1>Party — ${escapeHTML(location)}</h1>
  <p class="subtitle">${selected.length} member${selected.length === 1 ? '' : 's'}</p>
  ${members}
</body>
</html>`;
};

const savePartySheet = () => {
  const state = getState();
  const selected = getSelectedMembers(state);
  if (!selected.length) {
    alert('Select at least one member.');
    return;
  }
  const sheetWindow = window.open('', '_blank');
  if (!sheetWindow) {
    alert('Please allow pop-ups to open the party sheet.');
    return;
  }
  sheetWindow.document.open();
  sheetWindow.document.write(buildPartySheetHTML(state, selected));
  sheetWindow.document.close();
};

export const initPartyBuilder = () => {
  openPartyBtn.addEventListener('click', () => {
    const state = getState();
    const locations = Array.from(
      new Set(state.characters.map((character) => character.current_location).filter(Boolean))
    ).sort();
    if (!state.party.location && locations.length) setPartyLocation(locations[0]);
    renderPartyLists(getState());
    partyModal.showModal();
  });
  partyCloseBtn.addEventListener('click', () => partyModal.close());
  partyClearBtn.addEventListener('click', () => clearParty());
  partyLocationSelect.addEventListener('change', (event) => setPartyLocation(event.target.value));
  initFormPillGroups(partyModal);
  partyPlayerFilterGroup?.addEventListener('click', (event) => {
    if (!event.target.closest('.pill[data-pill-value]')) return;
    renderPartyLists(getState());
  });
  partyAvailable.addEventListener('click', handleListClick);
  partySelected.addEventListener('click', handleListClick);
  partyCopyBtn.addEventListener('click', copyPartySummary);
  partySaveBtn?.addEventListener('click', savePartySheet);
  partySelectAllBtn?.addEventListener('click', () => {
    const state = getState();
    const candidates = getFilteredCandidates(state);
    addPartyMembers(candidates.map((character) => character.slug));
  });

  subscribe((state) => renderPartyLists(state));
};
